import { useState } from 'react';
import {
  Box, Typography, Stack, Chip, alpha, useTheme, Skeleton,
  IconButton, Tooltip,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import AppsIcon from '@mui/icons-material/Apps';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WorkIcon from '@mui/icons-material/Work';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import FlagIcon from '@mui/icons-material/Flag';
import { useTranslation } from 'react-i18next';
import SearchBar from '../../components/DataTable/SearchBar/SearchBar';

const CATEGORY_COLOR_KEYS = {
  CONTRACTS: 'primary',
  OFFERS: 'warning',
  JOBS: 'success',
  INVOICES: 'info',
  APPLICATIONS: 'secondary',
};

const CATEGORY_ICONS = {
  ALL: AppsIcon,
  CONTRACTS: AssignmentIcon,
  OFFERS: LocalOfferIcon,
  JOBS: WorkIcon,
  INVOICES: ReceiptIcon,
  APPLICATIONS: PeopleIcon,
};

const CATEGORY_LABEL_KEYS = {
  CONTRACTS: 'alerts.category.contracts',
  OFFERS: 'alerts.category.offers',
  JOBS: 'alerts.category.jobs',
  INVOICES: 'alerts.category.invoices',
  APPLICATIONS: 'alerts.category.applications',
};

const SEVERITY_META = {
  critical: { icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} />, color: 'error' },
  warning:  { icon: <WarningAmberIcon sx={{ fontSize: 18 }} />, color: 'warning' },
  info:     { icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} />, color: 'info' },
};

const getRelativeTime = (dateStr, t) => {
  if (!dateStr) return null;
  const diffDays = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diffDays === 0) return t('alerts.today');
  if (diffDays === 1) return t('alerts.yesterday');
  return t('alerts.daysAgo', { count: diffDays });
};

// Backend evaluator'ları extra_data'ya iş/sözleşme/tarih vs. bilgisi koyabilir.
// Bu bilgiler mevcutsa expand-edilebilir bir "İncele" bölümünde gösterilir,
// yoksa detay bölümü çizilmez.
const hasDetailData = (extra) => {
  if (!extra || typeof extra !== 'object') return false;
  return Boolean(
    extra.job_title || extra.job_id ||
    extra.contract_description || extra.contract_id ||
    extra.contract_signing_date || extra.approval_date ||
    extra.contract_end_date || extra.last_invoice_date ||
    extra.next_renewal_date || extra.last_renewal_end_date ||
    extra.days_since_created || extra.days_since_approval ||
    extra.days_since_end || extra.days_since_update ||
    extra.days_stale || extra.days_overdue || extra.days_until_renewal ||
    extra.work_status || extra.job_status || extra.status_name ||
    extra.future_payments_count || extra.extension_count ||
    (Array.isArray(extra.related_jobs) && extra.related_jobs.length > 0)
  );
};

const formatDetailDate = (val, locale) => {
  if (!val) return null;
  try {
    return new Date(val).toLocaleDateString(locale, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return String(val);
  }
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (value == null || value === '') return null;
  return (
    <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
      <Icon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
      <Typography variant="caption" sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 700 }}>
        {label}:
      </Typography>
      <Typography variant="caption" sx={{ fontSize: 11.5, color: 'text.primary', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Typography>
    </Stack>
  );
};

const AlertCard = ({ instance, onDismiss, onUndismiss, isDismissed }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const severity = instance.alert_type_config?.severity || 'info';
  const category = instance.alert_type_config?.category || 'OTHER';
  const meta = SEVERITY_META[severity] || SEVERITY_META.info;
  const catColorKey = CATEGORY_COLOR_KEYS[category] || 'default';
  const paletteColor = theme.palette[meta.color]?.main || theme.palette.info.main;
  const relativeTime = getRelativeTime(instance.first_seen_at, t);
  const isCritical = severity === 'critical';
  const extra = instance.extra_data || {};
  const locale = t('common.locale', { defaultValue: 'tr-TR' });
  const showDetails = hasDetailData(extra);
  const relatedJobs = Array.isArray(extra.related_jobs) ? extra.related_jobs : [];

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 2,
        px: 2.5, py: 2, borderRadius: 2.5,
        border: '1px solid',
        borderLeft: `4px solid ${paletteColor}`,
        borderColor: alpha(paletteColor, isDismissed ? 0.12 : 0.25),
        bgcolor: alpha(paletteColor, isDismissed ? 0.015 : 0.03),
        opacity: isDismissed ? 0.7 : 1,
        transition: 'all 0.18s ease',
        position: 'relative',
        '&:hover': {
          borderColor: alpha(paletteColor, 0.5),
          boxShadow: `0 3px 16px ${alpha(paletteColor, 0.14)}`,
          transform: 'translateY(-1px)',
          opacity: 1,
        },
      }}
    >
      {/* Pulsing dot for critical */}
      {isCritical && !isDismissed && (
        <Box sx={{
          position: 'absolute', top: 10, right: 46,
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: 'error.main',
          animation: 'pulse 1.8s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.7)}` },
            '70%': { boxShadow: `0 0 0 7px ${alpha(theme.palette.error.main, 0)}` },
            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}` },
          },
        }} />
      )}

      {/* Dismiss/Undismiss button */}
      <Tooltip title={isDismissed ? t('alerts.undismiss') : t('alerts.dismiss')} arrow>
        <IconButton
          size="small"
          onClick={() => isDismissed ? onUndismiss?.(instance.id) : onDismiss?.(instance.id)}
          sx={{
            position: 'absolute', top: 8, right: 8,
            color: isDismissed ? 'success.main' : 'text.disabled',
            '&:hover': { color: isDismissed ? 'success.dark' : 'warning.main' },
          }}
        >
          {isDismissed ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>

      {/* Severity icon box */}
      <Box sx={{
        mt: 0.25, flexShrink: 0, width: 36, height: 36, borderRadius: 2,
        bgcolor: alpha(paletteColor, 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: paletteColor,
      }}>
        {meta.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" mb={0.5}>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13.5, lineHeight: 1.35 }}>
            {instance.alert_type_config?.label_tr || instance.alert_type_code}
          </Typography>
          <Chip
          onClick={() => {}}
            label={t(`alerts.severity.${severity}`)}
            size="small"
            color={meta.color}
            sx={{ height: 19, fontSize: 10.5, fontWeight: 700, px: 0.25 }}
          />
          <Chip
          onClick={() => {}}
            label={t(CATEGORY_LABEL_KEYS[category] || 'alerts.category.all', category)}
            size="small"
            color={catColorKey}
            variant="outlined"
            sx={{ height: 19, fontSize: 10.5, fontWeight: 600, px: 0.25 }}
          />
          {isDismissed && (
            <Chip
              onClick={() => {}}
              label={t('alerts.dismissed')}
              size="small"
              variant="outlined"
              sx={{ height: 19, fontSize: 10.5, fontWeight: 600, px: 0.25, borderColor: 'text.disabled', color: 'text.disabled' }}
            />
          )}
        </Stack>

        {(() => {
          // entity_label "Sözleşme #163" gibi generic gorunuyorsa (aciklama
          // bos ise backend fallback bunu koyuyor), daha okunabilir bir
          // ad turet: firma_adi + tur (Sozlesme/Is). Aciklama varsa oncelik.
          const raw = instance.entity_label;
          const isGeneric = typeof raw === 'string' && /^(Sözleşme|İş)\s+#/.test(raw);
          let display = raw;
          if (isGeneric && extra?.firm_name) {
            const kind = raw.startsWith('İş') ? 'İş' : 'Sözleşmesi';
            display = kind === 'İş'
              ? `${extra.firm_name} — ${raw}`
              : `${extra.firm_name} ${kind}`;
          }
          return display ? (
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, mb: 0.4 }}>
              {display}
            </Typography>
          ) : null;
        })()}

        {extra.firm_name && (
          <Stack direction="row" alignItems="center" gap={0.5} mb={0.4}>
            <BusinessIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600 }}>
              {extra.firm_name}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
          {relativeTime && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11.5 }}>
              {relativeTime}
            </Typography>
          )}
          {showDetails && (
            <Chip
              onClick={() => setExpanded(v => !v)}
              icon={expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              label={expanded ? t('alerts.hideDetails') : t('alerts.review')}
              size="small"
              variant="outlined"
              sx={{
                height: 20, fontSize: 11, fontWeight: 600,
                borderColor: alpha(paletteColor, 0.4),
                color: paletteColor,
                '& .MuiChip-icon': { color: paletteColor, ml: 0.5 },
              }}
            />
          )}
        </Stack>

        {expanded && showDetails && (
          <Box
            sx={{
              mt: 1.25, pt: 1.25, pl: 1.5, pr: 1,
              borderTop: '1px dashed',
              borderColor: alpha(paletteColor, 0.25),
            }}
          >
            <Stack gap={0.5}>
              <DetailRow
                icon={WorkIcon}
                label={t('alerts.detail.job')}
                value={extra.job_title || (extra.job_id ? `#${extra.job_id}` : null)}
              />
              <DetailRow
                icon={FlagIcon}
                label={t('alerts.detail.jobStatus')}
                value={extra.job_status ? t(`jobs.jobStatuses.${extra.job_status}`, { defaultValue: extra.job_status }) : null}
              />
              <DetailRow
                icon={AssignmentIcon}
                label={t('alerts.detail.contract')}
                value={
                  extra.contract_description
                  || (extra.firm_name && extra.contract_id
                    ? `${extra.firm_name} Sözleşmesi (#${extra.contract_id})`
                    : (extra.firm_name
                        ? `${extra.firm_name} Sözleşmesi`
                        : (extra.contract_id ? `Sözleşme #${extra.contract_id}` : null)))
                }
              />
              <DetailRow
                icon={EventIcon}
                label={t('alerts.detail.signingDate')}
                value={formatDetailDate(extra.contract_signing_date, locale)}
              />
              <DetailRow
                icon={EventIcon}
                label={t('alerts.detail.approvalDate')}
                value={formatDetailDate(extra.approval_date, locale)}
              />
              <DetailRow
                icon={EventIcon}
                label={t('alerts.detail.endDate')}
                value={formatDetailDate(extra.contract_end_date || extra.end_date, locale)}
              />
              <DetailRow
                icon={EventIcon}
                label={t('alerts.detail.nextRenewalDate')}
                value={formatDetailDate(extra.next_renewal_date, locale)}
              />
              <DetailRow
                icon={EventIcon}
                label={t('alerts.detail.lastInvoiceDate')}
                value={formatDetailDate(extra.last_invoice_date, locale)}
              />
              <DetailRow
                icon={DescriptionIcon}
                label={t('alerts.detail.workStatus')}
                value={extra.work_status || extra.status_name}
              />
              <DetailRow
                icon={InfoOutlinedIcon}
                label={t('alerts.detail.daysSinceUpdate')}
                value={
                  extra.days_since_update ?? extra.days_stale ?? extra.days_since_created ??
                  extra.days_since_approval ?? extra.days_since_end
                }
              />
              <DetailRow
                icon={InfoOutlinedIcon}
                label={t('alerts.detail.daysOverdue')}
                value={extra.days_overdue}
              />
              <DetailRow
                icon={InfoOutlinedIcon}
                label={t('alerts.detail.daysUntilRenewal')}
                value={extra.days_until_renewal}
              />
              <DetailRow
                icon={InfoOutlinedIcon}
                label={t('alerts.detail.futurePaymentsCount')}
                value={extra.future_payments_count}
              />
              {relatedJobs.length > 0 && (
                <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ minWidth: 0 }}>
                  <WorkIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0, mt: '3px' }} />
                  <Typography variant="caption" sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 700, flexShrink: 0 }}>
                    {t('alerts.detail.relatedJobs')}:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {relatedJobs.slice(0, 5).map((rj, idx) => (
                      <Chip
                        key={rj.id || idx}
                        onClick={() => {}}
                        label={rj.job_title || `#${rj.id}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10.5, fontWeight: 500 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CategoryPill = ({ cat, label, count, active, onClick, colorKey }) => {
  const theme = useTheme();
  const PillIcon = CATEGORY_ICONS[cat] || AppsIcon;
  const paletteColor = theme.palette[colorKey]?.main || theme.palette.primary.main;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75,
        px: 1.75, py: 0.9, borderRadius: 99, cursor: 'pointer',
        border: '1.5px solid',
        borderColor: active ? paletteColor : alpha(theme.palette.grey[500], 0.2),
        bgcolor: active ? alpha(paletteColor, 0.09) : 'transparent',
        transition: 'all 0.15s ease', userSelect: 'none',
        '&:hover': { borderColor: paletteColor, bgcolor: alpha(paletteColor, 0.06) },
      }}
    >
      <PillIcon sx={{ fontSize: 14, color: active ? paletteColor : 'text.disabled' }} />
      <Typography sx={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? paletteColor : 'text.secondary', lineHeight: 1, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Box sx={{
        bgcolor: active ? paletteColor : alpha(theme.palette.grey[500], 0.15),
        color: active ? 'white' : 'text.secondary',
        borderRadius: 99, px: 0.7, fontSize: 10.5, fontWeight: 700, lineHeight: 1.7, minWidth: 20, textAlign: 'center',
      }}>
        {count}
      </Box>
    </Box>
  );
};

const AlertInstanceList = ({ instances, loading, onDismiss, onUndismiss, isDismissedView = false }) => {
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [firmSearch, setFirmSearch] = useState('');
  const { t } = useTranslation();

  // Firma araması filtresi
  const searchFiltered = firmSearch.trim()
    ? instances.filter(i => {
        const q = firmSearch.trim().toLowerCase();
        const firmName = (i.extra_data?.firm_name || '').toLowerCase();
        const firmShort = (i.extra_data?.firm_short_name || '').toLowerCase();
        return firmName.includes(q) || firmShort.includes(q);
      })
    : instances;

  const categoryStats = ['ALL', ...Object.keys(CATEGORY_LABEL_KEYS)].map(cat => {
    const items = cat === 'ALL' ? searchFiltered : searchFiltered.filter(i => i.alert_type_config?.category === cat);
    return {
      cat,
      label: cat === 'ALL' ? t('alerts.category.all') : t(CATEGORY_LABEL_KEYS[cat]),
      count: items.length,
      colorKey: cat === 'ALL' ? 'primary' : (CATEGORY_COLOR_KEYS[cat] || 'default'),
    };
  }).filter(s => s.count > 0 || s.cat === 'ALL');

  const filtered = activeCategory === 'ALL'
    ? searchFiltered
    : searchFiltered.filter(i => i.alert_type_config?.category === activeCategory);

  if (loading) {
    return (
      <Stack gap={1.5}>
        {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={90} sx={{ borderRadius: 2.5 }} />)}
      </Stack>
    );
  }

  if (instances.length === 0) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: theme => alpha(theme.palette.primary.main, 0.07), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
          <NotificationsNoneIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
        </Box>
        <Typography fontWeight={700} sx={{ fontSize: 16, mb: 0.75 }}>
          {isDismissedView ? t('alerts.noDismissed') : t('alerts.noAlerts')}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 13 }}>
          {isDismissedView ? t('alerts.noDismissedDesc') : t('alerts.noAlertsDesc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Firma arama */}
      <Box sx={{ mb: 2.5 }}>
        <SearchBar
          value={firmSearch}
          onChange={setFirmSearch}
          placeholder={t('alerts.searchFirm')}
        />
      </Box>

      {/* Category filter pills */}
      <Stack direction="row" gap={1} flexWrap="wrap" mb={3}>
        {categoryStats.map(s => (
          <CategoryPill
            key={s.cat}
            cat={s.cat}
            label={s.label}
            count={s.count}
            active={activeCategory === s.cat}
            colorKey={s.colorKey}
            onClick={() => setActiveCategory(s.cat)}
          />
        ))}
      </Stack>

      {/* Flat alert list */}
      <Stack gap={1.25}>
        {filtered.map(inst => (
          <AlertCard
            key={inst.id}
            instance={inst}
            onDismiss={onDismiss}
            onUndismiss={onUndismiss}
            isDismissed={isDismissedView}
          />
        ))}
      </Stack>

      {filtered.length === 0 && (
        <Box sx={{ py: 7, textAlign: 'center' }}>
          <Typography variant="body2" color="text.disabled" fontWeight={500}>
            {firmSearch.trim() ? t('alerts.noSearchResults') : t('alerts.noCategoryAlerts')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AlertInstanceList;
